import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
// Lazy identity shared with the router (views/lazyViews) — a static import
// here pulled the `three` chunk back onto the landing critical path (A2).
import { FilesView, MarketplaceView } from '@/views/lazyViews';
import { ViewerRouteFallback } from '@/components/ViewerRouteFallback';
import { PromptView } from '@/views/PromptView';

import { Sidebar } from '../Sidebar';
import { TopToolbar } from '../TopToolbar';
import { FeatureTreeSidebar } from '../FeatureTreeSidebar';
import { BlankShellSidebar } from './BlankShellSidebar';
import { AppMenuBar } from '../AppMenuBar';
import { MenuBarErrorBoundary } from '../MenuBarErrorBoundary';
import { useHomepage } from '@/contexts/homepage-context';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn, editorUrl, extractEditorId } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  CONVERSATIONS_QUERY_KEY,
  CONVERSATIONS_STALE_TIME,
  makeConversationsQueryFn,
  THUMBNAIL_URLS_QUERY_KEY,
  THUMBNAIL_URLS_STALE_TIME,
  makeThumbnailUrlsQueryFn,
  THUMBNAIL_VIDEO_URLS_QUERY_KEY,
  THUMBNAIL_VIDEO_URLS_STALE_TIME,
  makeThumbnailVideoUrlsQueryFn,
} from '@/lib/conversationsQuery';

import { EditorTabPane } from './EditorTabPane';
import {
  createHomeTab,
  DEFAULT_TOOLBAR_STATE,
  getInitialTabsState,
  TOOLBAR_TOP,
  type FullTab,
} from './tabModel';

export function LayoutContent({
  isSidebarOpen,
  setIsSidebarOpen,
  onToggleRightSidebar,
}: {
  isSidebarOpen: boolean;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onToggleRightSidebar: () => void;
}) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    captureTabState,
    loadTabState,
    sidebarGenerateLoading,
    regenLoading,
    registerRegenAction,
    resetImplicitPrimitiveParams,
  } = useHomepage();

  // Pre-warm the conversations and thumbnail caches so Documents loads instantly.
  const { user } = useAuth();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!user) return;
    queryClient.prefetchQuery({
      queryKey: CONVERSATIONS_QUERY_KEY,
      staleTime: CONVERSATIONS_STALE_TIME,
      queryFn: makeConversationsQueryFn(user.id),
    });
    queryClient.prefetchQuery({
      queryKey: THUMBNAIL_URLS_QUERY_KEY(user.id),
      staleTime: THUMBNAIL_URLS_STALE_TIME,
      queryFn: makeThumbnailUrlsQueryFn(user.id),
    });
    queryClient.prefetchQuery({
      queryKey: THUMBNAIL_VIDEO_URLS_QUERY_KEY(user.id),
      staleTime: THUMBNAIL_VIDEO_URLS_STALE_TIME,
      queryFn: makeThumbnailVideoUrlsQueryFn(user.id),
    });
  }, [user, queryClient]);

  const isHome = location.pathname === '/';
  const isEditor = location.pathname.startsWith('/editor/');
  const isFiles =
    location.pathname === '/files' || location.pathname.startsWith('/files/');
  const isMarketplace =
    location.pathname === '/market' || location.pathname.startsWith('/market/');
  const showShell = isHome || isEditor || isFiles || isMarketplace;
  const conversationId = isEditor
    ? extractEditorId(location.pathname.split('/editor/')[1] ?? '')
    : null;

  const [{ tabs: initTabs, activeTabId: initActiveTabId }] =
    useState(getInitialTabsState);
  const [tabs, setTabs] = useState<FullTab[]>(initTabs);
  const [activeTabId, setActiveTabId] = useState<string>(initActiveTabId);
  // Track which tabs have been mounted at least once (lazy activation).
  // Tabs are added when first clicked and removed when closed.
  const [everActivated, setEverActivated] = useState<Set<string>>(
    () => new Set([initActiveTabId]),
  );
  // documentsOpen is URL-driven: it's true whenever the path starts with /files.
  // This way, folder navigation inside the Documents panel (which navigates to
  // /files/<slug>/...) keeps Documents open naturally instead of being reverted
  // by a back-navigation effect. Browser back/forward also Just Work — going
  // back from /files/<slug> to /editor/abc closes Documents automatically.
  const documentsOpen = isFiles;

  // Off-viewport horizontal scroll for the Documents / Marketplace panels.
  // These panels use a fixed-width grid that never reflows, so when the window
  // is narrower than the grid the extra columns spill off-screen. We let the
  // VIEWPORT scroller (<html>) own that horizontal overflow so its scrollbar
  // pins to the bottom of the window (reachable) instead of the bottom of the
  // very tall <body> (off-viewport). overflow-x:auto means the bar only shows
  // when the window is actually too narrow. Scoped to these panels so model
  // tabs keep the global overflow-x:hidden guard. See issue #47.
  useEffect(() => {
    const wantHScroll = showShell && (documentsOpen || isMarketplace);
    document.documentElement.classList.toggle('doc-hscroll', wantHScroll);
    return () => {
      document.documentElement.classList.remove('doc-hscroll');
    };
  }, [showShell, documentsOpen, isMarketplace]);

  // Snapshot of the previous render's isFiles. Used by the editor useEffect
  // to detect "we just navigated from /files into /editor/...", which is the
  // "click conversation from Documents" flow — it must spawn a new editor tab
  // rather than replace the active one.
  const prevIsFilesRef = useRef(isFiles);
  useEffect(() => {
    prevIsFilesRef.current = isFiles;
  }, [isFiles]);

  const tabsRef = useRef<FullTab[]>(tabs);
  tabsRef.current = tabs;
  const activeTabIdRef = useRef<string>(activeTabId);
  activeTabIdRef.current = activeTabId;

  useEffect(() => {
    localStorage.setItem('tk-tabs', JSON.stringify(tabs));
  }, [tabs]);
  useEffect(() => {
    if (activeTabId) localStorage.setItem('tk-active-tab', activeTabId);
  }, [activeTabId]);

  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    const activeTab = tabsRef.current.find(
      (t) => t.id === activeTabIdRef.current,
    );
    if (!activeTab) return;
    // Nav (vehicleEnabled/vehicleTab/vehicleSubTab/textToCadSelected) is now
    // rehydrated directly from the 'tk-vehicle-nav' localStorage key inside
    // HomepageProvider (issue #51). When that key is present it is the
    // authoritative source, so we must NOT overwrite the freshly-rehydrated
    // context with this tab's (possibly stale) snapshot — otherwise JAWS would
    // be reset to the homepage on every refresh. When the key is absent
    // (first run before Patch 1 ever wrote it, or localStorage disabled) we
    // fall back to the original tab-snapshot restore.
    let hasDirectNav = false;
    try {
      hasDirectNav = localStorage.getItem('tk-vehicle-nav') !== null;
    } catch {
      hasDirectNav = false;
    }
    if (!hasDirectNav) {
      loadTabState(activeTab.toolbarState);
    }
    // If the page loaded at a specific URL, respect it — don't overwrite it
    // with the stored tab state. Only redirect from root.
    if (location.pathname !== '/') return;
    if (activeTab.type === 'editor' && activeTab.conversationId) {
      navigate(editorUrl(activeTab.conversationId, activeTab.title));
    }
  }, [loadTabState, navigate, location.pathname]);

  const saveCurrentTab = () => {
    const captured = captureTabState();
    setTabs((prev) =>
      prev.map((t) =>
        t.id === activeTabIdRef.current && t.type === 'home'
          ? { ...t, toolbarState: captured }
          : t,
      ),
    );
    return captured;
  };

  const handleTabClick = (tabId: string) => {
    // Re-clicking the already-active tab is a no-op ONLY when we're actually
    // showing it. On Documents (/files) or Marketplace (/market) the tab's id is
    // still the stored activeTabId, so without the Documents/Marketplace guards
    // clicking it early-returns and you can't navigate back to that tab.
    if (tabId === activeTabIdRef.current && !documentsOpen && !isMarketplace)
      return;
    if (sidebarGenerateLoading || regenLoading) return;
    // No setDocumentsOpen needed — navigate() below takes us off /files,
    // which flips documentsOpen=false automatically via the derived value.
    saveCurrentTab();
    const newTab = tabsRef.current.find((t) => t.id === tabId);
    if (!newTab) return;
    if (newTab.type !== 'editor') {
      registerRegenAction({ fn: null, enabled: false, hasArtifact: false });
    }
    loadTabState(newTab.toolbarState);
    setActiveTabId(tabId);
    setEverActivated((prev) => new Set([...prev, tabId]));
    if (newTab.type === 'editor' && newTab.conversationId) {
      navigate(editorUrl(newTab.conversationId, newTab.title));
    } else {
      navigate('/');
    }
  };

  const handleTabClose = (tabId: string) => {
    const currentTabs = tabsRef.current;
    const closingIndex = currentTabs.findIndex((t) => t.id === tabId);
    const newTabs = currentTabs.filter((t) => t.id !== tabId);

    setEverActivated((prev) => {
      const next = new Set(prev);
      next.delete(tabId);
      return next;
    });

    if (newTabs.length === 0) {
      const freshTab = createHomeTab();
      setTabs([freshTab]);
      loadTabState(freshTab.toolbarState);
      // A fresh home tab is a clean slate — clear the GLOBAL implicit-primitive
      // selection so a prior lattice-converter upload doesn't bleed its standby
      // onto the new page (#309).
      resetImplicitPrimitiveParams();
      setActiveTabId(freshTab.id);
      setEverActivated(new Set([freshTab.id]));
      navigate('/');
      return;
    }

    setTabs(newTabs);

    if (tabId === activeTabIdRef.current) {
      const nextTab = newTabs[Math.min(closingIndex, newTabs.length - 1)];
      if (nextTab.type !== 'editor') {
        registerRegenAction({ fn: null, enabled: false, hasArtifact: false });
      }
      loadTabState(nextTab.toolbarState);
      setActiveTabId(nextTab.id);
      setEverActivated((prev) => new Set([...prev, nextTab.id]));
      if (nextTab.type === 'editor' && nextTab.conversationId) {
        navigate(editorUrl(nextTab.conversationId, nextTab.title));
      } else {
        navigate('/');
      }
    }
  };

  const handleNewTab = () => {
    if (tabsRef.current.length >= 5) return;
    // No setDocumentsOpen — navigate('/') below flips isFiles=false.
    saveCurrentTab();
    const newTab = createHomeTab();
    setTabs((prev) => [...prev, newTab]);
    registerRegenAction({ fn: null, enabled: false, hasArtifact: false });
    loadTabState(newTab.toolbarState);
    // A new home tab is a clean slate — clear the GLOBAL implicit-primitive
    // selection so a prior lattice-converter upload doesn't bleed its standby
    // onto the New Tab page (#309).
    resetImplicitPrimitiveParams();
    navigate('/');
    setActiveTabId(newTab.id);
    setEverActivated((prev) => new Set([...prev, newTab.id]));
  };

  const handleReorderTabs = (fromId: string, toId: string) => {
    setTabs((prev) => {
      const from = prev.findIndex((t) => t.id === fromId);
      const to = prev.findIndex((t) => t.id === toId);
      if (from === -1 || to === -1 || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  useEffect(() => {
    if (!isEditor || !conversationId) return;
    const currentActiveId = activeTabIdRef.current;
    const existingEditor = tabsRef.current.find(
      (t) => t.type === 'editor' && t.conversationId === conversationId,
    );
    if (existingEditor) {
      if (existingEditor.id !== currentActiveId) {
        setActiveTabId(existingEditor.id);
        setEverActivated((prev) => new Set([...prev, existingEditor.id]));
      }
    } else {
      const _activeTab = tabsRef.current.find((t) => t.id === currentActiveId);
      // prevIsFilesRef.current reflects the PREVIOUS render's isFiles, so
      // when we land at /editor/abc the ref tells us whether the prior URL
      // was a /files/... path (i.e. the user clicked a conversation from
      // inside the Documents panel).
      if (prevIsFilesRef.current) {
        // Opening from Documents panel: spawn a new editor tab. Documents
        // closes automatically because isFiles is now false (URL-driven).
        const newEditorTab: FullTab = {
          id: crypto.randomUUID(),
          type: 'editor',
          conversationId,
          title: 'Loading…',
          toolbarState: { ...DEFAULT_TOOLBAR_STATE },
        };
        setTabs((prev) => (prev.length < 5 ? [...prev, newEditorTab] : prev));
        setActiveTabId(newEditorTab.id);
        setEverActivated((prev) => new Set([...prev, newEditorTab.id]));
      } else {
        // Convert the active tab to an editor tab — it's already in everActivated
        setTabs((prev) =>
          prev.map((t) => {
            if (t.id !== currentActiveId) return t;
            return {
              ...t,
              type: 'editor' as const,
              conversationId,
              title: 'Loading…',
            };
          }),
        );
      }
    }
  }, [isEditor, conversationId]);

  useEffect(() => {
    if (isEditor) return;
    const currentTabs = tabsRef.current;
    const activeTab = currentTabs.find((t) => t.id === activeTabIdRef.current);
    if (activeTab && activeTab.type === 'editor') {
      const firstHomeTab = currentTabs.find((t) => t.type === 'home');
      if (firstHomeTab) {
        loadTabState(firstHomeTab.toolbarState);
        setActiveTabId(firstHomeTab.id);
      }
    }
  }, [isEditor, loadTabState]);

  // URL-driven toggle: navigate to /market to open, navigate away to close.
  const handleMarketplaceClick = () => {
    if (isMarketplace) {
      const activeTab = tabsRef.current.find(
        (t) => t.id === activeTabIdRef.current,
      );
      if (activeTab?.type === 'editor' && activeTab.conversationId) {
        navigate(editorUrl(activeTab.conversationId, activeTab.title));
      } else {
        navigate('/');
      }
    } else {
      navigate('/market');
    }
  };

  const handleDocumentsClick = () => {
    // URL-driven toggle: navigate to /files to open, or back to the active
    // tab's URL to close. The derived `documentsOpen` flips accordingly.
    if (documentsOpen) {
      const activeTab = tabsRef.current.find(
        (t) => t.id === activeTabIdRef.current,
      );
      if (activeTab?.type === 'editor' && activeTab.conversationId) {
        navigate(editorUrl(activeTab.conversationId, activeTab.title));
      } else {
        navigate('/');
      }
    } else {
      navigate('/files');
    }
  };

  return (
    <>
      <MenuBarErrorBoundary>
        <AppMenuBar
          onToggleRightSidebar={onToggleRightSidebar}
          isSidebarOpen={isSidebarOpen}
          showTabs={showShell}
          tabs={tabs}
          activeTabId={activeTabId}
          onTabClick={handleTabClick}
          onTabClose={handleTabClose}
          onNewTab={handleNewTab}
          onReorderTabs={handleReorderTabs}
          onDocumentsClick={handleDocumentsClick}
          isDocumentsActive={documentsOpen}
          onMarketplaceClick={handleMarketplaceClick}
          isMarketplaceActive={isMarketplace}
        />
      </MenuBarErrorBoundary>

      {showShell && <TopToolbar />}

      <div
        className={cn(
          'flex transition-all ease-in-out',
          // When the Documents panel is open we let the page grow past the
          // viewport and scroll via the BROWSER's main scrollbar — no inner
          // scroll container. Model tabs keep their fixed-viewport layout so
          // the 3D viewer's flex/sizing math is unchanged.
          showShell && !documentsOpen && !isMarketplace
            ? 'h-dvh overflow-hidden'
            : 'min-h-dvh',
        )}
        style={
          showShell && !isMobile
            ? { paddingTop: isFiles || isMarketplace ? '2.5rem' : TOOLBAR_TOP }
            : undefined
        }
      >
        {/* Documents and Marketplace are pure browsing surfaces — no model is
            loaded, so the Feature Tree has nothing to show there. They get a
            blank rail of the same footprint instead, so the content column
            sits in the identical place on both and never shifts against a
            model tab. The real Feature Tree renders on home + editor. */}
        {showShell &&
          (isFiles || isMarketplace ? (
            <BlankShellSidebar />
          ) : (
            <FeatureTreeSidebar />
          ))}

        <div
          className={cn(
            'relative flex-1 bg-[#4C5F69]',
            showShell && !documentsOpen && !isMarketplace
              ? 'h-full overflow-y-auto'
              : '',
          )}
        >
          {/* Documents panel — fixed-position, URL-driven */}
          {documentsOpen && showShell && (
            <div className="w-full">
              <Suspense fallback={<ViewerRouteFallback />}>
                <FilesView />
              </Suspense>
            </div>
          )}

          {/* Marketplace panel — fixed-position, URL-driven (same as Documents) */}
          {isMarketplace && showShell && (
            <div className="w-full">
              <Suspense fallback={<ViewerRouteFallback />}>
                <MarketplaceView />
              </Suspense>
            </div>
          )}

          {/* Regular tab panes — suppressed while Documents or Marketplace is open */}
          {tabs.map((tab) => {
            if (!everActivated.has(tab.id)) return null;
            const isVisible =
              showShell &&
              tab.id === activeTabId &&
              !documentsOpen &&
              !isMarketplace;
            return (
              <div
                key={tab.id}
                className="h-full w-full"
                style={{ display: isVisible ? undefined : 'none' }}
              >
                {tab.type === 'home' && (
                  <PromptView isActive={tab.id === activeTabId} />
                )}
                {tab.type === 'editor' && tab.conversationId && (
                  <EditorTabPane
                    conversationId={tab.conversationId}
                    setTabs={setTabs}
                    isActive={tab.id === activeTabId}
                  />
                )}
              </div>
            );
          })}
          {!showShell && <Outlet context={{ isSidebarOpen }} />}
        </div>

        <Sidebar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>
    </>
  );
}
